// Brand identity, derived from the request host at app startup.
//
// The seam exists at the UI layer only. Backend, DB, Stripe, and tRPC stay
// brand-unaware until specific behavior actually diverges. When a real
// divergence (e.g. B2B-only plans) lands, extend the seam one layer deeper
// then; do not preemptively thread `brand` through services that don't need
// it yet.

import {
  DEPLOYMENTS,
  type Deployment,
  TIER_OFFERS_PRODUCTION_LINK,
} from './deployment-hosts'
import {
  safeSessionGet,
  safeSessionRemove,
  safeSessionSet,
  safeStorageGet,
  safeStorageRemove,
  safeStorageSet,
} from './safe-storage'

export type BrandId = 'consumer' | 'business'

export interface Brand {
  id: BrandId
  name: string
  tagline: string
  /** The meta description every page carries unless it sets its own. */
  description: string
  /** The meta description of the landing page — what a search result shows. */
  landingDescription: string
  /** Every host that serves this brand, across every deployment. */
  hosts: readonly string[]
}

// Derived, never hand-written: a second declaration of which hosts belong to a
// brand is a second thing to keep in agreement with DEPLOYMENTS.
const consumer: Brand = {
  id: 'consumer',
  name: 'Stopal',
  tagline: 'Pallet storage that comes to you',
  description: 'Storage that comes to you. We deliver, you fill, we store.',
  landingDescription:
    'Door-to-door pallet storage and moving. Store It, Rent It, or Move It — pay once at checkout, no subscriptions.',
  hosts: DEPLOYMENTS.flatMap((d) => d.consumer),
}

// Also the business hero's opening paragraph, which renders it from here.
// Consumer's "pay once at checkout, no subscriptions" is that product's
// packaging, not this one's.
const BUSINESS_DESCRIPTION =
  'Store the assets your business depends on with Stopal, at your own locations, or anywhere in between. Know what you have, know where it is, and move it wherever it needs to go next.'

const business: Brand = {
  id: 'business',
  name: 'Stopal Business',
  tagline: 'Pallet storage and on-demand logistics for teams',
  description: BUSINESS_DESCRIPTION,
  landingDescription: BUSINESS_DESCRIPTION,
  hosts: DEPLOYMENTS.flatMap((d) => d.business ?? []),
}

export const brands: Record<BrandId, Brand> = { consumer, business }

const hostToBrand: Record<string, Brand> = Object.fromEntries(
  Object.values(brands).flatMap((brand) =>
    brand.hosts.map((host) => [host, brand] as const),
  ),
)

/**
 * Hosts where a `?brand=` override may be remembered durably.
 *
 * Everywhere else the override is remembered only for the TAB SESSION. The
 * override exists so a brand can be exercised before DNS is in place, but it
 * outranks the host lookup below and the brand now gates the whole
 * authenticated `/app` surface — so persisting it durably on a reachable
 * deployment turns one `?brand=business` link into a permanent shell swap for
 * that browser profile, with no in-app way back.
 *
 * Deliberately an allowlist of local hosts, NOT "any host absent from
 * `hostToBrand`": staging and preview deployments are real hosts people use,
 * and treating every unrecognised host as a dev sandbox is what makes the
 * one-way door reappear there.
 */
const DURABLE_OVERRIDE_HOSTS = new Set(['localhost', '127.0.0.1', '::1'])

const OVERRIDE_KEY = 'stopal:brand-override'

/**
 * hostname, not host.split(':') — the latter yields '[' for an IPv6 loopback
 * like [::1]:3100, so the local allowlist never matched there.
 */
function normalizeHost(host: string): string {
  return host.toLowerCase().replace(/^\[|\]$/g, '')
}

function currentHost(): string {
  return normalizeHost(window.location.hostname)
}

/** The brand a host name resolves to, with no override in play. */
export function hostBrandFor(host: string): Brand {
  return hostToBrand[normalizeHost(host)] ?? consumer
}

/**
 * The canonical Stopal Business host serving the SAME deployment as `host`, or
 * `null` when this deployment has no business host.
 *
 * `null` is not a failure — it is the instruction to promote a business user in
 * place (same origin, `?brand=business`) rather than send them somewhere that
 * does not resolve. Local development and any preview host land here.
 *
 * The answer is derived from DEPLOYMENTS rather than gated on a flag, so a
 * business host cannot be served without the redirect finding it, nor targeted
 * before it is declared. The deploy ordering that follows is real: point DNS
 * and issue the cert FIRST, then ship the build that names the host. The
 * reverse strands every business user on an origin that does not answer.
 */
export function businessHostFor(host: string): string | null {
  return deploymentFor(host)?.business?.[0] ?? null
}

/**
 * The consumer host of the deployment serving `host` — the site a customer
 * should land on from anything we hand out (a referral QR), even when an
 * operator made it while signed in on the business host. `null` for a host no
 * deployment declares (local, preview), where the caller keeps its own origin.
 */
export function consumerHostFor(host: string): string | null {
  return deploymentFor(host)?.consumer[0] ?? null
}

/** The deployment serving `host`, or `undefined` when no entry declares it. */
export function deploymentFor(host: string): Deployment | undefined {
  const normalized = normalizeHost(host)
  return DEPLOYMENTS.find(
    (d) =>
      d.consumer.includes(normalized) ||
      (d.business?.includes(normalized) ?? false),
  )
}

/**
 * The production host for `brandId`, when `host`'s tier offers a production
 * link — otherwise `null`.
 *
 * This is the staging badge's whole answer: "you are not on the live site, and
 * here is the live site for the thing you are looking at". Three separate
 * `null`s, all of them meaning "render nothing", and each is load-bearing:
 *
 * - **`host`'s tier offers no production link** — production itself, and any
 *   tier that has not opted in. Read from `TIER_OFFERS_PRODUCTION_LINK` rather
 *   than tested as "not production", so a new tier cannot inherit a badge.
 * - **`host` is declared by nobody** — a preview build, a local port, a host
 *   pointed here by DNS we do not control. Fails CLOSED rather than guessing:
 *   a host that cannot be placed must not be labelled "staging", and must never
 *   be handed a production link derived from that guess.
 * - **The production deployment has no host for that brand.** Cannot happen
 *   with today's table and is not asserted away, because the alternative is
 *   `!` on a lookup that a future table edit could genuinely empty.
 *
 * Takes the brand as a PARAMETER rather than deriving it from `host`, because a
 * `?brand=` override can make staging render the business landing on a consumer
 * hostname. The link has to follow what the visitor is actually looking at; a
 * business visitor sent to the consumer site is the failure mode to avoid.
 */
export function productionHostFor(
  host: string,
  brandId: BrandId,
): string | null {
  const deployment = deploymentFor(host)
  if (!deployment || !TIER_OFFERS_PRODUCTION_LINK[deployment.env]) return null

  const production = DEPLOYMENTS.find((d) => d.env === 'production')
  if (!production) return null

  return brandId === 'business'
    ? (production.business?.[0] ?? null)
    : production.consumer[0]
}

// Detection precedence: explicit ?brand= query → remembered override
// (durably on local hosts, per-tab elsewhere) → host lookup → consumer default.
export function detectBrand(): Brand {
  if (typeof window === 'undefined') return consumer

  const host = currentHost()
  const durable = DURABLE_OVERRIDE_HOSTS.has(host)
  const params = new URLSearchParams(window.location.search)
  const fromQuery = params.get('brand')

  if (fromQuery === 'business' || fromQuery === 'consumer') {
    if (durable) safeStorageSet(OVERRIDE_KEY, fromQuery)
    else safeSessionSet(OVERRIDE_KEY, fromQuery)
    return brands[fromQuery]
  }

  const stored = durable
    ? safeStorageGet(OVERRIDE_KEY)
    : safeSessionGet(OVERRIDE_KEY)
  if (stored === 'business' || stored === 'consumer') return brands[stored]

  // Retire any durable value written before the override was scoped, so a
  // profile poisoned by the old behaviour doesn't carry it to a dev host.
  if (!durable) safeStorageRemove(OVERRIDE_KEY)

  return hostBrandFor(host)
}

/** A page title suffixed with the brand the visitor is looking at. */
export function brandTitle(page: string): string {
  return `${page} — ${detectBrand().name}`
}

/**
 * The brand the HOST alone resolves to, ignoring any `?brand=` override.
 *
 * The product gate needs this to tell an override-induced brand (which it can
 * redirect away from) from a host-induced one (which it cannot — stripping the
 * param would land on the same host and resolve to the same brand, forever).
 */
export function detectHostBrand(): Brand {
  if (typeof window === 'undefined') return consumer
  return hostBrandFor(currentHost())
}

/** Forget a remembered `?brand=` override, in both storages. */
export function clearBrandOverride(): void {
  safeStorageRemove(OVERRIDE_KEY)
  safeSessionRemove(OVERRIDE_KEY)
}
